import { formatBlocks } from './budget-meter';

/**
 * FRONT-2670: what a non-admin reads instead of tokens. Eddy prices its own
 * calls in USD (catalogue rates, cache-aware); the platform's budget counts
 * compute blocks at a fixed rate, so a block here is the same unit the
 * budget meter shows. Every figure is an estimate — the platform's own meter
 * is the bill — hence the tilde.
 */
export const COMPUTE_BLOCKS_PER_USD = 100_000;

/** USD to compute blocks. Unpriced (null/undefined) stays unpriced, never 0. */
export function usdToComputeBlocks(usd: number | null | undefined): number | null {
  if (usd == null || !Number.isFinite(usd)) return null;
  return usd * COMPUTE_BLOCKS_PER_USD;
}

/**
 * "~35k CB", or a dash when unpriced. A priced figure under one block reads
 * "<1 CB": "~0 CB" would claim the call was free.
 */
export function formatEstimatedBlocks(blocks: number | null | undefined): string {
  if (blocks == null || !Number.isFinite(blocks)) return '—';
  if (blocks > 0 && blocks < 1) return '<1 CB';
  return `~${formatBlocks(carryUnit(blocks))} CB`;
}

/**
 * `formatBlocks` picks the unit before it rounds, which is fine for the
 * budget's whole numbers but turns a USD estimate just under a unit
 * boundary into "~1000 CB" or "~1000k CB". Lift such a figure to the
 * boundary so it reads "~1k CB" / "~1M CB".
 */
function carryUnit(blocks: number): number {
  if (blocks < 1_000 && Math.round(blocks) >= 1_000) return 1_000;
  if (blocks >= 1_000 && blocks < 1_000_000 && Math.round(blocks / 1_000) >= 1_000) return 1_000_000;
  return blocks;
}

/**
 * FRONT-2962: a coding figure the server already gave in compute blocks. A
 * charge the platform made reads plain ("1.09M CB"); an estimate keeps the
 * tilde ("~1.09M CB") and carries {@link ESTIMATE_HINT} on hover. The page
 * never converts a charge from dollars.
 */
export function formatComputeBlocks(blocks: number | null | undefined, estimated: boolean): string {
  if (estimated) return formatEstimatedBlocks(blocks);
  if (blocks == null || !Number.isFinite(blocks)) return '—';
  if (blocks > 0 && blocks < 1) return '<1 CB';
  return `${formatBlocks(carryUnit(blocks))} CB`;
}

/** The hover text on a coding figure that is still an estimate. */
export const ESTIMATE_HINT = 'Estimate. Final charge pending.';

/** The two steps above in one, for the many callers holding a USD figure. */
export function formatUsdAsBlocks(usd: number | null | undefined): string {
  return formatEstimatedBlocks(usdToComputeBlocks(usd));
}

/**
 * The partial-pricing note under a CB total: a number that silently leaves
 * out unpriced models reads as the whole bill. Null when everything was
 * priced (nothing to qualify) or nothing was (the figure is a dash, and
 * {@link UNPRICED_NOTE} says why).
 */
export function partialPricingNote(pricedTokens: number, unpricedTokens: number): string | null {
  if (unpricedTokens <= 0 || pricedTokens <= 0) return null;
  // Capped at 99: some usage WAS priced, or there would be no figure to qualify.
  const pct = Math.min(99, Math.round((unpricedTokens / (pricedTokens + unpricedTokens)) * 100));
  if (pct === 0) return 'A small share of usage is not priced yet, so the real figure is slightly higher.';
  return `About ${pct}% of usage is not priced yet, so the real figure is higher.`;
}

export const UNPRICED_NOTE = 'Not priced yet: these models have no published price.';

/**
 * FRONT-2701, shared by FRONT-2796: rows ranked by cost, biggest first.
 * Unpriced rows (and rows an older server sent with no cost) go last, by
 * tokens. Does not mutate. A table whose rows the server sends in token
 * order ranks through this while its compute-blocks column is shown; with it
 * hidden the token order stays, since ranking by an invisible number would
 * read as random.
 */
export function rankByCost<T extends { totalTokens: number; estimatedCostUsd?: number | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ca = a.estimatedCostUsd ?? null;
    const cb = b.estimatedCostUsd ?? null;
    if (ca == null && cb == null) return b.totalTokens - a.totalTokens;
    if (ca == null) return 1;
    if (cb == null) return -1;
    return cb - ca || b.totalTokens - a.totalTokens;
  });
}
